import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { registerUser, loginUser, requestOtp, verifyOtp, fetchCities, fetchAreas, fetchCategories, fetchAllTags } from '../services/api';
import { API_CONFIG } from '../config/api.config';
import { saveDeductedLocation, getDeductedLocation, clearDeductedLocation } from '../utils/locationStorage';
import { TextField, Select, MenuItem, FormControl, InputLabel, CircularProgress } from '@mui/material';

// Add CSS for loader and Responsive Layout
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes rotation {
    to { transform: rotate(360deg); }
  }
  
  /* Responsive grid handling */
  .auth-grid-wrapper {
    display: grid;
    grid-template-columns: 1fr; /* Mobile: Single column */
    gap: 24px;
    width: 100%;
    max-width: 1000px;
  }

  @media (min-width: 920px) {
    .auth-grid-wrapper {
      grid-template-columns: 1fr 1fr; /* Laptop/Desktop: Two columns */
    }
  }

  @media (max-width: 500px) {
    .auth-card {
      padding: 20px !important; /* Reduce padding on small mobile screens */
    }
    .auth-title {
      font-size: 20px !important;
    }
  }
`;
document.head.appendChild(styleSheet);

const Auth = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('login');
  const [vendorTab, setVendorTab] = useState('login');
  const [customerLoading, setCustomerLoading] = useState(false);
  const [vendorLoading, setVendorLoading] = useState(false);
  const [customerError, setCustomerError] = useState('');
  const [vendorError, setVendorError] = useState('');
  const [cities, setCities] = useState([]);
  const [areas, setAreas] = useState([]);
  const [categories, setCategories] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedVendorCity, setSelectedVendorCity] = useState('');
  const [categorySearch, setCategorySearch] = useState('');
  const [filteredCategorySuggestions, setFilteredCategorySuggestions] = useState([]);
  const [tagSearch, setTagSearch] = useState('');
  const [filteredTags, setFilteredTags] = useState([]);
  const [vendorTagsForReg, setVendorTagsForReg] = useState([]);
  const [userCoords, setUserCoords] = useState({ lat: null, lng: null });
  const [deductedLocation, setDeductedLocation] = useState(null);
  const [locationExpiringSoon, setLocationExpiringSoon] = useState(false);

  const [customerForm, setCustomerForm] = useState({
    name: '', phone: '', password: '', confirmPassword: '', selectedServices: [],
  });

  const [vendorForm, setVendorForm] = useState({
    name: '', phone: '', password: '', confirmPassword: '',
    business_name: '', address: '', city_id: '', area_id: '', selectedServices: [],
  });

  useEffect(() => {
    const loadLocationData = async () => {
      try {
        // Check for previously saved location
        const saved = getDeductedLocation();
        if (saved) {
          setDeductedLocation(saved);
          setVendorForm(prev => ({
            ...prev,
            city_id: saved.city_id?.toString() || '',
            area_id: saved.area_id?.toString() || ''
          }));
          setSelectedVendorCity(saved.city_id?.toString() || '');
        }

        const [citiesData, areasData, categoriesData, tagsData] = await Promise.all([fetchCities(), fetchAreas(), fetchCategories(), fetchAllTags()]);
        setCities(Array.isArray(citiesData) ? citiesData : []);
        setAreas(Array.isArray(areasData) ? areasData : []);
        setCategories(Array.isArray(categoriesData) ? categoriesData : []);
        setAllTags(Array.isArray(tagsData) ? tagsData : []);

        // Auto-fill city/area from geolocation
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            (pos) => {
              setUserCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
              reverseGeocodeAndAutoFillCity(pos.coords.latitude, pos.coords.longitude, areasData, citiesData);
            },
            () => console.log('Geolocation not available')
          );
        }
      } catch (err) {
        console.error('Failed to load location data:', err);
      }
    };
    loadLocationData();
  }, []);

  const reverseGeocodeAndAutoFillCity = async (lat, lng) => {
    try {
      // Call YOUR OWN backend instead of Nominatim directly
      const response = await fetch(`${API_CONFIG.BASE_URL}/detect-location?lat=${lat}&lng=${lng}`);
      const data = await response.json();

      if (data.city_id) {
        // Save deducted location with timestamp
        saveDeductedLocation({
          city_id: data.city_id,
          city_name: data.city_name,
          area_id: data.area_id,
          area_name: data.area_name,
          lat,
          lng
        });
        setDeductedLocation(data);
        setSelectedVendorCity(data.city_id.toString());
        setVendorForm(prev => ({
          ...prev,
          city_id: data.city_id.toString(),
          area_id: data.area_id ? data.area_id.toString() : ''
        }));
      }
    } catch (err) {
      console.error('Location detection failed:', err);
    }
  };

  // Monitor location expiry
  useEffect(() => {
    if (!deductedLocation) return;

    const interval = setInterval(() => {
      const saved = getDeductedLocation();
      if (!saved) {
        setDeductedLocation(null);
        setLocationExpiringSoon(false);
        clearInterval(interval);
      } else {
        // Show warning when less than 1 minute remaining
        const stored = localStorage.getItem('deducted_location');
        if (stored) {
          const data = JSON.parse(stored);
          const elapsed = Date.now() - data.timestamp;
          const remaining = 5 * 60 * 1000 - elapsed;
          setLocationExpiringSoon(remaining < 60 * 1000);
        }
      }
    }, 10000); // Check every 10 seconds

    return () => clearInterval(interval);
  }, [deductedLocation]);

  useEffect(() => {
    if (!categorySearch.trim()) {
      setFilteredCategorySuggestions([]);
      return;
    }
    const search = categorySearch.toLowerCase();
    const results = categories.filter(cat => cat.name.toLowerCase().includes(search));
    setFilteredCategorySuggestions(results);
  }, [categorySearch, categories]);

  useEffect(() => {
    if (tagSearch.trim().length > 0) {
      const search = tagSearch.toLowerCase();
      const filtered = allTags.filter(tag =>
        tag.name.toLowerCase().includes(search) &&
        !vendorTagsForReg.some(selected => selected.id === tag.id)
      );
      setFilteredTags(filtered.slice(0, 8));
    } else {
      setFilteredTags([]);
    }
  }, [tagSearch, allTags, vendorTagsForReg]);

  const handleCategorySelectFromSearch = (categoryId) => {
    const selectedCategory = categories.find(cat => cat.id === categoryId);
    if (!selectedCategory) return;

    setVendorForm(prev => {
      let updated = [...prev.selectedServices];
      if (selectedCategory.parent_id) {
        const parentId = selectedCategory.parent_id;
        if (!updated.includes(parentId)) updated.push(parentId);
      }
      if (!updated.includes(categoryId)) updated.push(categoryId);
      return { ...prev, selectedServices: updated };
    });
    setCategorySearch('');
    setFilteredCategorySuggestions([]);
  };

  const handleCustomerInputChange = (e) => {
    const { name, value } = e.target;
    setCustomerForm(prev => ({ ...prev, [name]: value }));
  };

  const handleVendorInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'city_id') {
      setSelectedVendorCity(value);
      setVendorForm(prev => ({ ...prev, [name]: value, area_id: '' }));
    } else {
      setVendorForm(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleVendorServiceToggle = (categoryId) => {
    setVendorForm(prev => {
      const updated = [...prev.selectedServices];
      const index = updated.indexOf(categoryId);
      if (index > -1) updated.splice(index, 1);
      else updated.push(categoryId);
      return { ...prev, selectedServices: updated };
    });
  };

  const getParentCategories = () => categories.filter(cat => !cat.parent_id || cat.parent_id === null);
  const getChildCategories = (parentId) => categories.filter(cat => cat.parent_id === parentId);

  const getFlattenedCategories = (selectedServices) => {
    const result = [];
    getParentCategories().forEach(parent => {
      result.push(parent);
      if (selectedServices.includes(parent.id)) {
        const children = getChildCategories(parent.id);
        result.push(...children);
      }
    });
    return result;
  };

  const handleTagSelect = (tag) => {
    setVendorTagsForReg(prev => [...prev, tag]);
    setTagSearch('');
    setFilteredTags([]);
  };

  const handleTagRemove = (tagId) => {
    setVendorTagsForReg(prev => prev.filter(tag => tag.id !== tagId));
  };

  const handleTagInputKeyDown = async (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const tagName = tagSearch.trim();
      if (tagName && !vendorTagsForReg.some(t => t.name.toLowerCase() === tagName.toLowerCase())) {
        // Check if tag exists in allTags
        const existingTag = allTags.find(t => t.name.toLowerCase() === tagName.toLowerCase());
        if (existingTag) {
          handleTagSelect(existingTag);
        } else {
          // For new tags, create a temporary object (will be handled by backend)
          const newTag = { id: null, name: tagName, temp: true };
          setVendorTagsForReg(prev => [...prev, newTag]);
          setTagSearch('');
        }
      }
    }
  };

  const validatePhone = (phone) => /^\d{10}$/.test(phone.replace(/\D/g, ''));

  const handleAuthSubmit = async (e, userType, isRegister) => {
    e.preventDefault();
    const isCustomer = userType === 'customer';
    const setError = isCustomer ? setCustomerError : setVendorError;
    const setLoadingState = isCustomer ? setCustomerLoading : setVendorLoading;
    const formData = isCustomer ? customerForm : vendorForm;

    setError('');

    if (!validatePhone(formData.phone)) {
      setError('Enter valid 10-digit phone number');
      return;
    }
    if (!formData.password || formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (isRegister) {
      if (!formData.name) { setError('Name is required'); return; }
      if (formData.password !== formData.confirmPassword) { setError('Passwords do not match'); return; }
      if (!isCustomer) {
        if (!formData.business_name) { setError('Business name is required'); return; }
        if (!formData.address) { setError('Address is required'); return; }
        if (!formData.city_id) { setError('Please select a city'); return; }
        if (!formData.area_id) { setError('Please select an area'); return; }
        if (!formData.selectedServices || formData.selectedServices.length === 0) { setError('Please select at least one service'); return; }
      }
    }

    setLoadingState(true);
    try {
      if (isRegister) {
        const userData = {
          name: formData.name,
          phone: formData.phone,
          password: formData.password,
          user_type: userType,
          business_name: formData.business_name || '',
          address: formData.address || '',
          city_id: formData.city_id || '',
          area_id: formData.area_id || '',
          lat: userCoords.lat,
          lng: userCoords.lng,
        };

        // Include categories and tags if vendor
        if (!isCustomer) {
          userData.category_ids = formData.selectedServices || [];

          // Include ALL tags (both new and existing)
          // For existing tags: { id: number, name: string }
          // For new tags: { id: null, name: string }
          if (vendorTagsForReg.length > 0) {
            userData.tags = vendorTagsForReg.map(tag => ({
              id: tag.id || null,
              name: tag.name
            }));
          }
        }

        const res = await registerUser(userData);
        localStorage.setItem('user', JSON.stringify(res.user));
        window.dispatchEvent(new Event('userLogin'));
        navigate(res.user.user_type === 'vendor' ? '/vendor-dashboard' : '/services');
      } else {
        const res = await loginUser(formData.phone, formData.password);
        localStorage.setItem('user', JSON.stringify(res.user));
        window.dispatchEvent(new Event('userLogin'));
        navigate(res.user.user_type === 'vendor' ? '/vendor-dashboard' : '/services');
      }
    } catch (err) {
      setError(err.message || (isRegister ? 'Registration failed' : 'Login failed'));
    } finally {
      setLoadingState(false);
    }
  };

  const filteredAreas = areas.filter(a => a.city_id === parseInt(selectedVendorCity));

  return (
    <div style={styles.pageContainer}>
      <div className="auth-grid-wrapper">
        {/* ═══ CUSTOMER AUTH CARD ═══ */}
        <div className="auth-card" style={styles.card}>
          <div style={styles.header}>
            <div style={styles.logoBadge}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
              </svg>
            </div>
            <h2 className="auth-title" style={styles.cardTitle}>Customer</h2>
            <p style={styles.cardSubtitle}>Browse and book services</p>
          </div>

          {customerError && <div style={styles.errorBanner}>{customerError}</div>}

          <form onSubmit={(e) => handleAuthSubmit(e, 'customer', activeTab === 'register')} style={styles.form}>
            {activeTab === 'register' && (
              <TextField fullWidth label="Full Name" name="name" placeholder="John Doe" value={customerForm.name} onChange={handleCustomerInputChange} variant="standard" size="small" />
            )}
            <TextField fullWidth label="Phone Number" name="phone" placeholder="9876543210" value={customerForm.phone} onChange={handleCustomerInputChange} variant="standard" size="small" type="tel" />
            <TextField fullWidth label="Password" name="password" placeholder="••••••••" value={customerForm.password} onChange={handleCustomerInputChange} variant="standard" size="small" type="password" />
            {activeTab === 'register' && (
              <TextField fullWidth label="Confirm Password" name="confirmPassword" placeholder="••••••••" value={customerForm.confirmPassword} onChange={handleCustomerInputChange} variant="standard" size="small" type="password" />
            )}
            <button type="submit" disabled={customerLoading} style={{ ...styles.submitBtn, ...(customerLoading ? styles.btnDisabled : {}) }}>
              {customerLoading ? <CircularProgress size={18} sx={{ color: 'white' }} /> : (activeTab === 'login' ? 'Sign In' : 'Create Account')}
            </button>
            <div style={styles.footer}>
              {activeTab === 'login' ? (
                <p>New here? <span onClick={() => setActiveTab('register')} style={styles.link}>Create account</span></p>
              ) : (
                <p>Already have account? <span onClick={() => setActiveTab('login')} style={styles.link}>Sign in</span></p>
              )}
            </div>
          </form>
        </div>

        {/* ═══ VENDOR AUTH CARD ═══ */}
        <div className="auth-card" style={styles.card}>
          <div style={styles.header}>
            <div style={{ ...styles.logoBadge, background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', boxShadow: '0 8px 12px -3px rgba(217, 119, 6, 0.3)' }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2m-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
              </svg>
            </div>
            <h2 className="auth-title" style={styles.cardTitle}>Vendor</h2>
            <p style={styles.cardSubtitle}>List and manage services</p>
          </div>

          {vendorError && <div style={styles.errorBanner}>{vendorError}</div>}

          <form onSubmit={(e) => handleAuthSubmit(e, 'vendor', vendorTab === 'register')} style={styles.form}>
            {vendorTab === 'register' && (
              <>
                <TextField fullWidth label="Full Name" name="name" placeholder="John Doe" value={vendorForm.name} onChange={handleVendorInputChange} variant="standard" size="small" />
                <TextField fullWidth label="Business Name" name="business_name" placeholder="Your Business" value={vendorForm.business_name} onChange={handleVendorInputChange} variant="standard" size="small" />
              </>
            )}
            <TextField fullWidth label="Phone Number" name="phone" placeholder="9876543210" value={vendorForm.phone} onChange={handleVendorInputChange} variant="standard" size="small" type="tel" />
            <TextField fullWidth label="Password" name="password" placeholder="••••••••" value={vendorForm.password} onChange={handleVendorInputChange} variant="standard" size="small" type="password" />
            {vendorTab === 'register' && (
              <>
                <TextField fullWidth label="Confirm Password" name="confirmPassword" placeholder="••••••••" value={vendorForm.confirmPassword} onChange={handleVendorInputChange} variant="standard" size="small" type="password" />
                <TextField fullWidth label="Full Address" name="address" placeholder="Street address" value={vendorForm.address} onChange={handleVendorInputChange} variant="standard" size="small" />
                {deductedLocation && (
                  <div style={{ background: '#ecfdf5', border: '1px solid #86efac', borderRadius: '6px', padding: '12px', marginBottom: '16px', fontSize: '13px', color: '#166534' }}>
                    📍 <strong>Location detected from device:</strong> {deductedLocation.city_name}{deductedLocation.area_name ? `, ${deductedLocation.area_name}` : ''}
                    {locationExpiringSoon && <span style={{ color: '#dc2626', marginLeft: '8px' }}>⚠️ Expires soon</span>}
                  </div>
                )}
                <div style={styles.grid2}>
                  <FormControl fullWidth size="small" disabled={!deductedLocation}>
                    <InputLabel>City</InputLabel>
                    <Select name="city_id" value={vendorForm.city_id} onChange={handleVendorInputChange} label="City">
                      <MenuItem value="">Select</MenuItem>
                      {cities.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <FormControl fullWidth size="small" disabled={!selectedVendorCity || !deductedLocation}>
                    <InputLabel>Area</InputLabel>
                    <Select name="area_id" value={vendorForm.area_id} onChange={handleVendorInputChange} label="Area">
                      <MenuItem value="">Select</MenuItem>
                      {filteredAreas.map(a => <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>)}
                    </Select>
                  </FormControl>
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Business Category <span style={{ color: '#ef4444' }}>*</span></label>
                  <TextField fullWidth placeholder="Search services..." value={categorySearch} onChange={(e) => setCategorySearch(e.target.value)} variant="standard" size="small" sx={{ mb: 1 }} />
                  {filteredCategorySuggestions.length > 0 && (
                    <div style={styles.suggestionBox}>
                      {filteredCategorySuggestions.slice(0, 8).map(cat => (
                        <div key={cat.id} onClick={() => handleCategorySelectFromSearch(cat.id)} style={styles.suggestionItem}>{cat.name}</div>
                      ))}
                    </div>
                  )}
                  <div style={styles.categoryContainer}>
                    {getFlattenedCategories(vendorForm.selectedServices).map(cat => (
                      <button key={cat.id} type="button" onClick={() => handleVendorServiceToggle(cat.id)} style={{ ...styles.categoryPill, ...(vendorForm.selectedServices.includes(cat.id) ? styles.categoryPillActive : {}) }}>{cat.name}</button>
                    ))}
                  </div>
                </div>

                <div style={styles.inputGroup}>
                  <label style={styles.label}>Tags (Optional - Press Enter to add)</label>
                  <TextField fullWidth placeholder="Search or type a tag and press Enter..." value={tagSearch} onChange={(e) => setTagSearch(e.target.value)} onKeyDown={handleTagInputKeyDown} variant="standard" size="small" sx={{ mb: 1 }} />

                  {filteredTags.length > 0 && (
                    <div style={styles.suggestionBox}>
                      {filteredTags.map(tag => (
                        <div key={tag.id} onClick={() => handleTagSelect(tag)} style={styles.suggestionItem}>{tag.name}</div>
                      ))}
                    </div>
                  )}

                  <div style={styles.categoryContainer}>
                    {vendorTagsForReg.map(tag => (
                      <button key={tag.id || tag.name} type="button" onClick={(e) => { e.preventDefault(); handleTagRemove(tag); }} style={{ ...styles.categoryPill, ...styles.categoryPillActive }}>
                        {tag.name} ✕
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}

            <button type="submit" disabled={vendorLoading} style={{ ...styles.submitBtn, ...{ background: '#f59e0b', boxShadow: '0 8px 12px -3px rgba(217, 119, 6, 0.35)' }, ...(vendorLoading ? styles.btnDisabled : {}) }}>
              {vendorLoading ? <CircularProgress size={18} sx={{ color: 'white' }} /> : (vendorTab === 'login' ? 'Sign In' : 'Get Started')}
            </button>

            <div style={styles.footer}>
              {vendorTab === 'login' ? (
                <p>New vendor? <span onClick={() => setVendorTab('register')} style={{ ...styles.link, color: '#f59e0b' }}>Register now</span></p>
              ) : (
                <p>Already have account? <span onClick={() => setVendorTab('login')} style={{ ...styles.link, color: '#f59e0b' }}>Sign in</span></p>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// --- Updated Styles Object (Responsive Defaults) ---
const styles = {
  pageContainer: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%)',
    padding: '40px 16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  card: {
    background: '#ffffff',
    borderRadius: '20px',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.08)',
    overflow: 'hidden',
    padding: '32px',
    width: '100%',
    boxSizing: 'border-box',
  },
  header: { textAlign: 'center', marginBottom: '24px' },
  logoBadge: {
    width: '56px',
    height: '56px',
    background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
    borderRadius: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 12px',
    boxShadow: '0 8px 12px -3px rgba(79, 70, 229, 0.25)',
  },
  cardTitle: { fontSize: '22px', fontWeight: '800', color: '#1e293b', marginBottom: '4px' },
  cardSubtitle: { color: '#64748b', fontSize: '13px' },
  form: { display: 'flex', flexDirection: 'column', gap: '14px' },
  grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' },
  inputGroup: { display: 'flex', flexDirection: 'column', gap: '6px' },
  label: { fontSize: '12px', fontWeight: '700', color: '#475569', marginLeft: '2px' },
  submitBtn: {
    marginTop: '6px',
    padding: '12px',
    borderRadius: '10px',
    border: 'none',
    background: '#4f46e5',
    color: '#ffffff',
    fontWeight: '700',
    fontSize: '15px',
    cursor: 'pointer',
    boxShadow: '0 8px 12px -3px rgba(79, 70, 229, 0.35)',
    transition: 'all 0.2s',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  btnDisabled: { opacity: 0.6, cursor: 'not-allowed' },
  errorBanner: {
    background: '#fef2f2',
    color: '#dc2626',
    padding: '10px 12px',
    borderRadius: '8px',
    fontSize: '12px',
    fontWeight: '500',
    marginBottom: '14px',
    border: '1px solid #fee2e2',
  },
  footer: { textAlign: 'center', marginTop: '14px', fontSize: '13px', color: '#64748b' },
  link: { color: '#4f46e5', fontWeight: '700', cursor: 'pointer' },
  suggestionBox: {
    border: '1px solid #e2e8f0',
    borderRadius: '10px',
    background: '#fff',
    maxHeight: '160px',
    overflowY: 'auto',
    marginBottom: '8px',
    boxShadow: '0 6px 12px rgba(0,0,0,0.05)',
  },
  suggestionItem: { padding: '10px 12px', cursor: 'pointer', fontSize: '13px', borderBottom: '1px solid #f1f5f9' },
  categoryContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    padding: '12px',
    background: '#f8fafc',
    borderRadius: '10px',
    border: '1.5px solid #e2e8f0',
    minHeight: '50px',
  },
  categoryPill: {
    padding: '8px 12px',
    borderRadius: '20px',
    border: '1.5px solid #cbd5e1',
    background: '#ffffff',
    color: '#475569',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  categoryPillActive: { background: '#4f46e5', color: '#ffffff', borderColor: '#4f46e5' }
};

export default Auth;